import type { PlatformName } from '../api/types.js';

/**
 * Setup instructions shown in the Console (ARCHITECTURE.md §65, §18).
 *
 * These exist because an operator who clicks Connect and hits a provider error
 * has no way to know why. Every one of these steps was learned by actually
 * doing it during this build — the Discord redirect URI, the TikTok client
 * key/secret confusion, the YouTube API that must be enabled separately.
 *
 * The rule for this file: describe only what is TRUE today. A step that
 * promises a capability the platform does not grant is worse than no help,
 * because the operator will spend real time looking for it.
 */

export type HelpStep = {
  text: string;
  /** A value the operator must copy exactly, rendered as code. */
  code?: string;
};

export type PlatformHelp = {
  /** One sentence: what connecting this platform actually gets you. */
  summary: string;
  /** Where the credentials come from. */
  portalUrl: string;
  portalLabel: string;
  steps: HelpStep[];
  /**
   * The things that most often go wrong. Shown as callouts.
   *
   * A LIST, because Discord has two hazards of exactly the same shape — the
   * Message Content Intent and channel permissions — and each produces a
   * connection that reports healthy while delivering nothing. Keeping only one
   * would have meant choosing which silent failure an operator gets to find out
   * about by losing an afternoon.
   */
  gotchas?: string[];
  /** Stated plainly when the platform cannot deliver chat. */
  limitation?: string;
};

const REDIRECT = (platform: string): string =>
  `https://dev.connaro.net/api/v1/oauth/${platform}/callback`;

export const PLATFORM_HELP: Partial<Record<PlatformName, PlatformHelp>> = {
  twitch: {
    summary: 'Live chat from your Twitch channel, delivered over EventSub.',
    portalUrl: 'https://dev.twitch.tv/console/apps',
    portalLabel: 'Twitch Developer Console',
    steps: [
      { text: 'Register an application in the Twitch Developer Console.' },
      { text: 'Add this exact redirect URI:', code: REDIRECT('twitch') },
      { text: 'Copy the Client ID and generate a Client Secret.' },
      { text: 'Give both to whoever runs this Connaro deployment.' },
      { text: 'Return here and click Connect Twitch.' },
    ],
    gotchas: [
      'The redirect URI must match character for character — including https and the trailing path. A mismatch fails on Twitch before Connaro is involved.',
    ],
  },

  youtube: {
    summary: 'Live chat from your YouTube broadcasts, read by polling.',
    portalUrl: 'https://console.cloud.google.com',
    portalLabel: 'Google Cloud Console',
    steps: [
      { text: 'Create a project in the Google Cloud Console.' },
      { text: 'Enable the "YouTube Data API v3" for that project.' },
      { text: 'Configure the OAuth consent screen.' },
      { text: 'Create an OAuth 2.0 Client ID of type "Web application".' },
      { text: 'Add this exact redirect URI:', code: REDIRECT('youtube') },
    ],
    gotchas: [
      'Enabling the YouTube Data API is a separate step from creating the credentials. Skip it and authorization succeeds while every later call fails.',
    ],
    limitation:
      'Until this deployment passes Google verification, only accounts added as test users can authorize. Everyone else sees "Error 403: access_denied".',
  },

  kick: {
    summary: 'Live chat from your Kick channel, delivered as signed webhooks.',
    portalUrl: 'https://kick.com/settings/developer',
    portalLabel: 'Kick Developer settings',
    steps: [
      { text: 'Open Account Settings, then the Developer tab.' },
      { text: 'Create an application.' },
      { text: 'Add this exact redirect URI:', code: REDIRECT('kick') },
      { text: 'Copy the Client ID and Client Secret.' },
    ],
    gotchas: [
      'Kick pushes chat to Connaro rather than Connaro fetching it, so this deployment must be reachable from the internet over HTTPS.',
    ],
  },

  blaze: {
    summary: 'Live chat from your Blaze.Stream channel.',
    portalUrl: 'https://dev.blaze.stream',
    portalLabel: 'Blaze developer portal',
    steps: [
      { text: 'Register an application.' },
      { text: 'Add this exact redirect URI:', code: REDIRECT('blaze') },
      { text: 'Copy the Client ID and Client Secret.' },
    ],
    gotchas: [
      'Blaze allows a limited number of simultaneous connections per application across the whole deployment, not per workspace.',
    ],
  },

  discord: {
    summary: 'Chat from channels you choose, inside a Discord server you administer.',
    portalUrl: 'https://discord.com/developers/applications',
    portalLabel: 'Discord Developer Portal',
    steps: [
      { text: 'Create an application, then add a Bot to it.' },
      {
        text: 'On the Bot tab, enable the Message Content Intent under Privileged Gateway Intents.',
      },
      { text: 'On the OAuth2 tab, add this exact redirect URI:', code: REDIRECT('discord') },
      { text: 'Click Connect below and choose the server to add the bot to.' },
      { text: 'Then use "Choose channels" to pick which channels Connaro reads.' },
      {
        text: 'For any PRIVATE channel you want read, give the Connaro bot the View Channel permission on that channel in Discord. Adding the bot to a server does not grant it access to restricted channels.',
      },
    ],
    gotchas: [
      'Without the Message Content Intent, Discord delivers every message with empty text. The connection looks healthy and the chat feed stays blank.',
      'A channel the bot cannot see sends Connaro nothing at all — Discord emits no event for it, so there is nothing to receive. Connaro marks those channels as hidden in "Choose channels", but the fix is in Discord: add the bot, or its role, to the channel with View Channel.',
    ],
    limitation:
      'You need the Manage Server permission in the server you attach. Connaro checks this with your own Discord account, so nobody can connect a server they do not administer.',
  },

  tiktok: {
    summary: 'Confirms your TikTok account. No chat.',
    portalUrl: 'https://developers.tiktok.com',
    portalLabel: 'TikTok for Developers',
    steps: [
      { text: 'Create an application and enable Login Kit.' },
      { text: 'Request the user.info.basic scope.' },
      { text: 'Add the redirect URI this deployment is configured with.' },
      { text: 'In Sandbox, add your TikTok account as a target user.' },
    ],
    gotchas: [
      'A sandbox application only accepts authorization from accounts listed as target users. Anyone else is refused by TikTok before Connaro sees the attempt.',
    ],
    limitation:
      'TikTok publishes no API for reading LIVE chat, so no messages will appear in Connaro Chat. Connecting confirms the account and nothing more.',
  },

  facebook: {
    summary: 'Live comments on a Page broadcast. Not yet available.',
    portalUrl: 'https://developers.facebook.com/apps',
    portalLabel: 'Meta for Developers',
    steps: [
      { text: 'Create an app and add the Facebook Login and Live Video products.' },
      { text: 'Add yourself to the app with a role, so it can be tested during review.' },
      { text: 'You will need a Facebook Page you administer — comments are read through a Page.' },
    ],
    limitation:
      'Reading Page content requires Meta App Review. The API exists and the integration is designed; access does not yet.',
  },

  rumble: {
    summary: 'Live chat and rants from your Rumble stream, read only.',
    portalUrl: 'https://rumble.com/account/livestream-api',
    portalLabel: 'Rumble Live Stream API settings',
    steps: [
      { text: 'Open the Live Stream API page in your Rumble account settings.' },
      { text: 'Generate (or copy) your Live Stream API URL.' },
      { text: 'Paste that URL into the field on the Rumble card and click Connect Rumble.' },
    ],
    gotchas: [
      'That URL contains your live stream key, not a read-only token — anyone who has it can control your broadcast the way Rumble itself would. Share it only with someone you would trust with your broadcast, and treat it the way you would a password.',
    ],
    limitation:
      'Rumble publishes no API for deleting messages, timing out, or banning, so Connaro Chat will show Rumble messages but the Moderator cannot act on them.',
  },
  instagram: {
    summary: 'Live comments from your Instagram Live, in the unified feed.',
    portalUrl: 'https://www.instagram.com/accounts/convert_to_professional_account/',
    portalLabel: 'Instagram Professional account settings',
    steps: [
      {
        text: 'Make sure your Instagram account is a Professional account — Business or Creator. A personal account cannot connect.',
      },
      { text: 'Click Connect Instagram and approve the permissions Instagram shows you.' },
      { text: 'Start an Instagram Live. Comments appear in Connaro Chat as viewers post them.' },
    ],
    gotchas: [
      'Comments only arrive while you are actually live. Instagram sends nothing between broadcasts, so a quiet feed between streams is expected rather than a fault.',
      'Comments on your ordinary posts are a different thing and deliberately do not appear here — Connaro subscribes only to live comments, so your feed stays your live chat.',
    ],
    limitation:
      'Instagram publishes an API for live comments and for nothing else about a broadcast. Likes, viewer joins, the viewer list, gifts and badges are not available to any application, so they will never appear in Connaro. Connaro also cannot post replies back to Instagram.',
  },
};

/** Whether the Console has setup guidance for a platform. */
export function hasHelp(platform: PlatformName): boolean {
  return PLATFORM_HELP[platform] !== undefined;
}
